import { ProductBehavior } from '@up2stay/up2promo-api-client';
import { Button, Card, CardContent, cn } from '@up2stay/up2promo-ui';
import { useReducedMotion } from 'motion/react';
import { useMemo, useState } from 'react';

import { useLocale } from '@/core/hooks/use-locale';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import type { Dictionary } from '@/core/providers/dictionary-provider';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths, TABS_PREFIX } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';

import { DashboardGreetingHeader } from './dashboard-greeting-header';
import {
  DashboardHeuteZuTun,
  DashboardHeuteZuTunSkeleton,
} from './dashboard-heute-zu-tun';
import {
  DashboardKpiStrip,
  type DashboardKpiItem,
} from './dashboard-kpi-strip';
import { DashboardLinkTargetsCard } from './dashboard-link-targets-card';
import { DashboardLiveCard } from './dashboard-live-card';
import { DashboardReputationCard } from './dashboard-reputation-card';
import { DashboardStandorteCard } from './dashboard-standorte-card';
import { sparklineValues } from './dashboard-status-helpers';
import type {
  DashboardPeriodChange,
  DashboardRangeDays,
} from './dashboard-status-types';
import { DashboardTapsScansCard } from './dashboard-taps-scans-card';
import { DashboardTopBusinessCardsCard } from './dashboard-top-business-cards-card';
import { useDashboardStatus } from './use-dashboard-status';

function greetingForHour(
  hour: number,
  templates: { morning: string; afternoon: string; evening: string },
  firstName: string,
): string {
  const template =
    hour < 12
      ? templates.morning
      : hour < 18
        ? templates.afternoon
        : templates.evening;
  return template.replace('{name}', firstName);
}

function formatBoardDate(date: Date, locale: string, short: boolean): string {
  if (short) {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
    }).format(date);
  }
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
}

function formatPeriodChangeLabel(
  change: DashboardPeriodChange,
  templates: {
    vsPrevious: string;
    vsPreviousNew: string;
    vsPreviousFlat: string;
  },
): string {
  if (change.direction === 'new') {
    return templates.vsPreviousNew;
  }
  if (change.direction === 'flat' || change.percent == null) {
    return templates.vsPreviousFlat;
  }
  const sign = change.percent > 0 ? '+' : '';
  const pct = `${sign}${change.percent.toFixed(Math.abs(change.percent) >= 10 ? 0 : 1)}%`;
  return templates.vsPrevious.replace('{pct}', pct);
}

/** Equal-width mid row: 1 / 2 / 3 visible modules (rep, links, live). */
function midRowGridClass(visibleCount: number): string {
  if (visibleCount <= 1) return 'grid-cols-1';
  if (visibleCount === 2) return 'grid-cols-1 md:grid-cols-2';
  return 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3';
}

/**
 * Main + 372px rail at xl only when both peers are present; otherwise
 * collapse to a single full-width column (no vacant sidebar slot).
 */
function railRowGridClass(hasMain: boolean, hasRail: boolean): string {
  if (hasMain && hasRail) {
    return 'grid-cols-1 xl:grid-cols-[minmax(0,1fr)_372px]';
  }
  return 'grid-cols-1';
}

/**
 * Status Board — approved desktop/mobile comps with adaptive columns when
 * BFF modules are permission/entitlement-omitted (null).
 * Desktop: KPIs → taps|heute → reputation|links|live → standorte|top cards
 * Mobile: greeting → range → KPIs → heute → taps → reputation → links → live → standorte
 */
export function DashboardStatusBoard() {
  const dict = useDictionary();
  const d = dict.dashboard;
  const boardDict = d.board;
  const productsEdit = dict.products?.edit;
  const repInsights = ((dict as Dictionary).reputation?.dashboard?.insights ??
    {}) as Record<string, string>;
  const { navigateShell, navigateShellDeepLink } = useNavigateShell();
  const isMobile = useIsMobile();
  const locale = useLocale();
  const reduceMotion = useReducedMotion() ?? false;
  const user = useAuthStore((s) => s.user);
  const [rangeDays, setRangeDays] = useState<DashboardRangeDays>(30);

  const firstName = user?.firstName?.trim() || boardDict.greeting.fallbackName;

  const statusLabels = useMemo(
    () => ({
      workspaceHealth: d.health.workspace,
      unrepliedCritical: d.health.unrepliedCritical,
      ratingLabel: d.health.rating,
      scoreLabel: d.health.score,
      reviewsCount: d.health.reviewsCount,
      linkedLocations: d.health.linkedLocations,
      zeroReviews: d.health.zeroReviews,
      zeroReviewsMeta: d.health.zeroReviewsMeta,
      setupConnectGoogle: d.doNext.setupConnectGoogle,
      setupReconnectGoogle: d.doNext.setupReconnectGoogle,
      setupLinkLocations: d.doNext.setupLinkLocations,
      setupLinkLocationsOne: d.doNext.setupLinkLocationsOne,
      setupConnectGoogleDesc: d.doNext.setupConnectGoogleDesc,
      setupReconnectGoogleDesc: d.doNext.setupReconnectGoogleDesc,
      setupLinkLocationsDesc: d.doNext.setupLinkLocationsDesc,
      doNextCriticalReviews: d.doNext.criticalReviews,
      doNextCriticalReviewsOne: d.doNext.criticalReviewsOne,
      doNextCriticalReviewsDesc: d.doNext.criticalReviewsDesc,
      doNextSyncFailed: d.doNext.syncFailed,
      doNextSyncFailedOne: d.doNext.syncFailedOne,
      doNextSyncFailedDesc: d.doNext.syncFailedDesc,
      doNextZeroReviews: d.doNext.zeroReviews,
      doNextZeroReviewsOne: d.doNext.zeroReviewsOne,
      doNextZeroReviewsDesc: d.doNext.zeroReviewsDesc,
      doNextNewLeads: d.doNext.newLeads,
      doNextNewLeadsOne: d.doNext.newLeadsOne,
      doNextNewLeadsDesc: d.doNext.newLeadsDesc,
      doNextCreateProfile: d.doNext.createProfile,
      doNextCreateProfileDesc: d.doNext.createProfileDesc,
      doNextAddLocation: d.doNext.addLocation,
      doNextAddLocationDesc: d.doNext.addLocationDesc,
      doNextAddProduct: d.doNext.addProduct,
      doNextAddProductDesc: d.doNext.addProductDesc,
      doNextOpenFeedback: d.doNext.openFeedback,
      doNextOpenFeedbackDesc: d.doNext.openFeedbackDesc,
      doNextInsightFallback: d.doNext.insightFallback,
      scans30d: d.live.scans30d,
      syncFailed: d.health.syncFailed,
    }),
    [d],
  );

  const board = useDashboardStatus(statusLabels, repInsights, rangeDays);

  const now = useMemo(() => new Date(), []);
  const greeting = greetingForHour(
    now.getHours(),
    boardDict.greeting,
    firstName,
  );
  const dateLabel = formatBoardDate(now, locale, isMobile);

  const openHref = (href: string) => {
    if (href.startsWith(`${TABS_PREFIX}/`) || href.startsWith('/')) {
      if (href.includes('://') || href.startsWith('up2promo:')) {
        navigateShellDeepLink(href);
        return;
      }
      navigateShell(href);
      return;
    }
    navigateShellDeepLink(href);
  };

  const periodChangeTemplates = boardDict.kpi.period;
  const tapsPeriodLabel = formatPeriodChangeLabel(
    board.productMetrics.periodChange,
    periodChangeTemplates,
  );
  const profileViewsPeriodLabel = formatPeriodChangeLabel(
    board.profileViewMetrics.periodChange,
    periodChangeTemplates,
  );

  const linkTypeLabels = useMemo(
    (): Record<ProductBehavior, string> => ({
      [ProductBehavior.PROFILE]:
        productsEdit?.typeProfile ?? boardDict.linkTargets.types.PROFILE,
      [ProductBehavior.GOOGLE_REVIEW]:
        productsEdit?.typeGoogleReview ??
        boardDict.linkTargets.types.GOOGLE_REVIEW,
      [ProductBehavior.URL]:
        productsEdit?.typeUrl ?? boardDict.linkTargets.types.URL,
      [ProductBehavior.INSTAGRAM]:
        productsEdit?.typeInstagram ?? boardDict.linkTargets.types.INSTAGRAM,
      [ProductBehavior.FACEBOOK]:
        productsEdit?.typeFacebook ?? boardDict.linkTargets.types.FACEBOOK,
      [ProductBehavior.TIKTOK]:
        productsEdit?.typeTiktok ?? boardDict.linkTargets.types.TIKTOK,
      [ProductBehavior.MENU]:
        productsEdit?.typeMenu ?? boardDict.linkTargets.types.MENU,
    }),
    [boardDict.linkTargets.types, productsEdit],
  );

  const kpiItems = useMemo((): DashboardKpiItem[] => {
    const scans = board.productMetrics.totalScans;
    const scanSpark =
      board.productMetrics.scanSeries.length > 0
        ? sparklineValues(
            board.productMetrics.scanSeries.map((p) => ({ value: p.scans })),
          )
        : undefined;
    const leadsSpark =
      board.leadsCreatedSeries.length > 0
        ? sparklineValues(board.leadsCreatedSeries)
        : undefined;
    const reviewSpark =
      board.reviewVolumeSeries.length > 0
        ? sparklineValues(board.reviewVolumeSeries)
        : undefined;
    const feedbackSpark =
      board.feedbackEntrySeries.length > 0
        ? sparklineValues(board.feedbackEntrySeries)
        : undefined;
    const rating =
      board.reputationEnabled &&
      board.overview &&
      board.overview.totalReviews > 0
        ? board.overview.averageRating.toFixed(1).replace('.', ',')
        : '—';
    const reviewCount = board.overview?.totalReviews ?? 0;
    const feedbackOpen = board.feedbackStats?.unrepliedCount ?? 0;
    const feedbackAvg = board.feedbackStats?.averageStars;

    const items: DashboardKpiItem[] = [];
    if (board.canReadProducts) {
      items.push({
        id: 'taps',
        label: boardDict.kpi.taps,
        value: String(scans),
        sub: boardDict.kpi.tapsSub,
        spark: scanSpark,
        periodChange: board.productMetrics.periodChange,
        periodChangeLabel: tapsPeriodLabel,
        tone: 'brand',
        href: shellPaths.moreProducts,
      });
    }
    if (board.canReadLeads) {
      // Leads captured in the selected range (same series as the sparkline);
      // the badge counts those still waiting in status "new" (DEV-550).
      const leadsCreated = board.leadsCreatedSeries.reduce(
        (sum, point) => sum + point.value,
        0,
      );
      items.push({
        id: 'leads',
        label: boardDict.kpi.leads,
        value: String(leadsCreated),
        sub: boardDict.kpi.leadsSub,
        badge:
          board.newLeadsCount > 0
            ? boardDict.kpi.leadsBadge.replace(
                '{count}',
                String(board.newLeadsCount),
              )
            : undefined,
        badgeTone: 'ok',
        spark: leadsSpark,
        tone: 'ok',
        href: shellPaths.leads,
      });
    }
    if (board.reputationEnabled) {
      items.push({
        id: 'rating',
        label: isMobile ? boardDict.kpi.rating : boardDict.kpi.ratingDesktop,
        value: rating,
        sub: boardDict.kpi.ratingSub.replace('{count}', String(reviewCount)),
        spark: reviewSpark,
        tone: 'rating',
        href: shellPaths.reputationDashboard,
      });
    }
    if (board.feedbackEnabled) {
      items.push({
        id: 'feedback',
        label: boardDict.kpi.feedback,
        value: String(feedbackOpen),
        sub:
          feedbackAvg != null
            ? boardDict.kpi.feedbackSubAvg.replace(
                '{stars}',
                feedbackAvg.toFixed(1).replace('.', ','),
              )
            : boardDict.kpi.feedbackSub,
        badge:
          (board.feedbackStats?.newCount ?? 0) > 0
            ? boardDict.kpi.feedbackBadge.replace(
                '{count}',
                String(board.feedbackStats?.newCount ?? 0),
              )
            : undefined,
        badgeTone: 'warning',
        spark: feedbackSpark,
        tone: 'warning',
        href: shellPaths.feedbackActivity,
      });
    }
    return items;
  }, [
    board.canReadLeads,
    board.canReadProducts,
    board.feedbackEnabled,
    board.feedbackEntrySeries,
    board.feedbackStats?.averageStars,
    board.feedbackStats?.newCount,
    board.feedbackStats?.unrepliedCount,
    board.leadsCreatedSeries,
    board.newLeadsCount,
    board.overview,
    board.productMetrics.periodChange,
    board.productMetrics.scanSeries,
    board.productMetrics.totalScans,
    board.reputationEnabled,
    board.reviewVolumeSeries,
    boardDict.kpi,
    isMobile,
    tapsPeriodLabel,
  ]);

  const showProductsModules = board.canReadProducts;
  const showReputationModule = board.reputationEnabled;
  const showLocationsModule = board.canReadLocations || board.reputationEnabled;
  const showTopCardsModule = showProductsModules && !isMobile;
  const midRowVisibleCount =
    (showReputationModule ? 1 : 0) + (showProductsModules ? 2 : 0);

  const topCardRows = useMemo(
    () =>
      board.productMetrics.topBusinessCards.map((row) => ({
        ...row,
        tapsLabel: boardDict.topCards.tapsLabel.replace(
          '{count}',
          String(row.taps),
        ),
      })),
    [board.productMetrics.topBusinessCards, boardDict.topCards.tapsLabel],
  );

  const openCountLabel = boardDict.heute.openCount.replace(
    '{count}',
    String(board.doNextItems.length),
  );

  const heuteBlock = board.isLoading ? (
    <DashboardHeuteZuTunSkeleton label={d.hero.loading} />
  ) : (
    <DashboardHeuteZuTun
      title={boardDict.heute.title}
      openCountLabel={openCountLabel}
      empty={d.doNext.empty}
      items={board.doNextItems}
      onOpen={openHref}
      reduceMotion={reduceMotion}
      isMobile={isMobile}
    />
  );

  const tapsBlock = (
    <DashboardTapsScansCard
      scanSeries={board.productMetrics.scanSeries}
      previousScanSeries={board.productMetrics.previousScanSeries}
      totalScans={board.productMetrics.totalScans}
      previousPeriodScans={board.productMetrics.previousPeriodScans}
      scanSources={board.productMetrics.scanSources}
      rangeDays={rangeDays}
      periodChange={board.productMetrics.periodChange}
      periodChangeLabel={tapsPeriodLabel}
      activeProductsCount={board.productMetrics.activeProductsCount}
      profileViews={board.profileViewMetrics}
      canReadProfiles={board.canReadProfiles}
      canManageProducts={board.canManageProducts}
      profileViewsPeriodChangeLabel={profileViewsPeriodLabel}
      isLoading={board.isLoading}
      locale={locale}
      isMobile={isMobile}
      onOpen={openHref}
      labels={{
        title: boardDict.taps.title,
        subtitle: boardDict.taps.subtitle,
        subtitleDesktop: boardDict.taps.subtitleDesktop,
        empty: boardDict.taps.empty,
        emptyDesc: boardDict.taps.emptyDesc,
        emptyCta: boardDict.taps.emptyCta,
        productTaps: boardDict.taps.productTaps,
        strongestDay: boardDict.taps.strongestDay,
        strongestDayValue: boardDict.taps.strongestDayValue,
        strongestDayValueDesktop: boardDict.taps.strongestDayValueDesktop,
        strongestDayTaps: boardDict.taps.strongestDayTaps,
        strongestDayTapsOne: boardDict.taps.strongestDayTapsOne,
        strongestDayViews: boardDict.taps.strongestDayViews,
        strongestDayViewsOne: boardDict.taps.strongestDayViewsOne,
        avgPerDay: boardDict.taps.avgPerDay,
        avgPerDayValue: boardDict.taps.avgPerDayValue,
        avgViewsPerDay: boardDict.taps.avgViewsPerDay,
        avgViewsPerDayValue: boardDict.taps.avgViewsPerDayValue,
        activeProducts: boardDict.taps.activeProducts,
        activeProductsValue: boardDict.taps.activeProductsValue,
        legendPrevious: boardDict.taps.legendPrevious,
        scanSourceQr: boardDict.taps.scanSourceQr,
        scanSourceNfc: boardDict.taps.scanSourceNfc,
        scanSourceUnknown: boardDict.taps.scanSourceUnknown,
        profileViewsTitle: boardDict.taps.profileViewsTitle,
        profileViewsEmpty: boardDict.taps.profileViewsEmpty,
        profileViewsEmptyDesc: boardDict.taps.profileViewsEmptyDesc,
        profileViewsEmptyCta: boardDict.taps.profileViewsEmptyCta,
        profileViewsOpen: boardDict.taps.profileViewsOpen,
        profileViewsSourceWallet: boardDict.taps.profileViewsSourceWallet,
        profileViewsSourceQr: boardDict.taps.profileViewsSourceQr,
        profileViewsSourceDirect: boardDict.taps.profileViewsSourceDirect,
        profileViewsSourceProduct: boardDict.taps.profileViewsSourceProduct,
        profileViewsFootnote: boardDict.taps.profileViewsFootnote,
      }}
    />
  );

  const reputationBlock = (
    <DashboardReputationCard
      overview={board.overview}
      reputationEnabled={board.reputationEnabled}
      isLoading={board.isLoading}
      isMobile={isMobile}
      onOpen={openHref}
      labels={{
        title: boardDict.reputation.title,
        openAll: boardDict.reputation.openAll,
        openAllDesktop: boardDict.reputation.openAllDesktop,
        score: boardDict.reputation.score,
        of100: boardDict.reputation.of100,
        reviewsCount: boardDict.reputation.reviewsCount,
        responseRate: boardDict.reputation.responseRate,
        responseRateWarn: boardDict.reputation.responseRateWarn,
        empty: boardDict.reputation.empty,
      }}
    />
  );

  const linkTargetsBlock = (
    <DashboardLinkTargetsCard
      links={board.productMetrics.linkTargets}
      isLoading={board.isLoading}
      onManage={() => openHref(shellPaths.moreProducts)}
      labels={{
        title: boardDict.linkTargets.title,
        subtitle: boardDict.linkTargets.subtitle,
        manage: boardDict.linkTargets.manage,
        empty: boardDict.linkTargets.empty,
        footer: boardDict.linkTargets.footer,
        productsCount: boardDict.linkTargets.productsCount,
        typeLabels: linkTypeLabels,
      }}
    />
  );

  const liveBlock = (
    <DashboardLiveCard
      liveScans={board.liveScans}
      highlightedScanId={board.highlightedScanId}
      connectionState={board.connectionState}
      locale={locale}
      onOpen={openHref}
      reduceMotion={reduceMotion}
      labels={{
        title: boardDict.live.title,
        empty: boardDict.live.empty,
        connecting: d.live.connecting,
        connected: boardDict.live.connected,
        error: d.live.error,
        scanSub: boardDict.live.scanSub,
      }}
    />
  );

  const standorteBlock = (
    <DashboardStandorteCard
      rows={board.healthRows}
      mix={board.healthMix}
      isLoading={board.isLoading}
      isMobile={isMobile}
      onOpen={openHref}
      onViewAll={() =>
        openHref(
          board.reputationEnabled
            ? shellPaths.reputationDashboard
            : shellPaths.moreWorkspaceLocations,
        )
      }
      labels={{
        title: boardDict.locationsTitle,
        viewAll: boardDict.standorte.viewAll,
        empty: board.canManageLocations
          ? d.health.empty
          : d.health.emptyReadOnly,
        emptyCta: d.health.emptyCta,
        colLocation: boardDict.standorte.colLocation,
        colScore: boardDict.standorte.colScore,
        colRating: boardDict.standorte.colRating,
        colScans: boardDict.standorte.colScans,
        reply: boardDict.standorte.reply,
        connectGoogle: boardDict.standorte.connectGoogle,
        open: boardDict.standorte.open,
        mixOk: d.health.mix.ok,
        mixAttention: d.health.mix.attention,
        canManage: board.canManageLocations,
      }}
    />
  );

  const topCardsBlock = !isMobile ? (
    <DashboardTopBusinessCardsCard
      rows={topCardRows}
      isLoading={board.isLoading}
      onOpenTeam={() => openHref(shellPaths.morePeople)}
      labels={{
        title: boardDict.topCards.title,
        team: boardDict.topCards.team,
        empty: boardDict.topCards.empty,
      }}
    />
  ) : null;

  return (
    <div className="flex flex-col gap-4 md:gap-[22px]">
      {(board.locationsError || board.overviewError || board.leadsError) && (
        <Card className="border-destructive/30">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <p className="text-sm text-muted-foreground">
              {d.error.loadFailed}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="min-h-11"
              onClick={() => board.refetchAll()}
            >
              {d.error.retry}
            </Button>
          </CardContent>
        </Card>
      )}

      <DashboardGreetingHeader
        dateLabel={dateLabel}
        greeting={greeting}
        rangeDays={rangeDays}
        onRangeChange={setRangeDays}
        rangeLabels={{
          7: boardDict.range.d7,
          30: boardDict.range.d30,
          90: boardDict.range.d90,
        }}
        rangeAriaLabel={boardDict.range.aria}
        notificationsLabel={boardDict.notifications}
        unreadCount={board.unreadCount}
        onOpenNotifications={() => openHref(shellPaths.moreUserNotifications)}
        setupProductLabel={boardDict.setupProduct}
        onSetupProduct={() => openHref(shellPaths.moreProductsSetup)}
        showSetupProduct={!isMobile && board.canManageProducts}
        isMobile={isMobile}
      />

      {(board.isLoading || kpiItems.length > 0) && (
        <DashboardKpiStrip
          items={kpiItems}
          isLoading={board.isLoading}
          isMobile={isMobile}
          rangeDays={rangeDays}
          onOpen={openHref}
          loadingLabel={boardDict.kpi.aria}
        />
      )}

      {isMobile ? (
        <div className="flex flex-col gap-4">
          {heuteBlock}
          {showProductsModules ? tapsBlock : null}
          {showReputationModule ? reputationBlock : null}
          {showProductsModules ? linkTargetsBlock : null}
          {showProductsModules ? liveBlock : null}
          {showLocationsModule ? standorteBlock : null}
        </div>
      ) : (
        <>
          {/* Row 2: ~2/3 taps + ~1/3 heute — equal height. `xl:` matches
              shell sidebar reserved-space (1280) so the 372px rail fits.
              Collapses to full-width when products (left peer) is omitted. */}
          <div
            className={cn(
              'grid min-w-0 items-stretch gap-3.5',
              railRowGridClass(showProductsModules, true),
            )}
          >
            {showProductsModules ? (
              <div className="min-w-0">{tapsBlock}</div>
            ) : null}
            <div className="min-w-0">{heuteBlock}</div>
          </div>
          {/* Row 3: equal columns for visible peers only (no empty slots). */}
          {(showReputationModule || showProductsModules) && (
            <div
              className={cn(
                'grid min-w-0 items-stretch gap-3.5',
                midRowGridClass(midRowVisibleCount),
              )}
            >
              {showReputationModule ? (
                <div className="min-w-0">{reputationBlock}</div>
              ) : null}
              {showProductsModules ? (
                <div className="min-w-0">{linkTargetsBlock}</div>
              ) : null}
              {showProductsModules ? (
                <div className="min-w-0">{liveBlock}</div>
              ) : null}
            </div>
          )}
          {/* Row 4: ~2/3 standorte + ~1/3 top cards — rail only when both
              peers exist; otherwise the remaining card spans full width. */}
          {(showLocationsModule || showTopCardsModule) && (
            <div
              className={cn(
                'grid min-w-0 items-stretch gap-3.5',
                railRowGridClass(showLocationsModule, showTopCardsModule),
              )}
            >
              {showLocationsModule ? (
                <div className="min-w-0">{standorteBlock}</div>
              ) : null}
              {showTopCardsModule ? (
                <div className="min-w-0">{topCardsBlock}</div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}
